import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Users, UserCheck, Calendar, DollarSign } from "lucide-react";

const AdminHomePage = () => {
  const [stats, setStats] = useState({
    contestants: 0,
    members: 0,
    playdays: 0,
    totalFees: 0
  });
  const [recentPlaydays, setRecentPlaydays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contestantsRes, membersRes, playdaysRes, entriesRes] = await Promise.all([
        axios.get(`${API}/contestants`),
        axios.get(`${API}/members`),
        axios.get(`${API}/playdays`),
        axios.get(`${API}/entries`)
      ]);

      const totalFees = entriesRes.data
        .filter(e => e.is_paid)
        .reduce((sum, e) => sum + e.total_fees, 0);

      setStats({
        contestants: contestantsRes.data.length,
        members: membersRes.data.length,
        playdays: playdaysRes.data.length,
        totalFees
      });

      setRecentPlaydays(playdaysRes.data.slice(0, 5));
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-home">
      <div>
        <h1 className="font-heading font-bold text-3xl text-text-primary">Dashboard</h1>
        <p className="text-text-secondary mt-1">Welcome to TTP Play Day Admin</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="h-6 w-6" />}
          label="Total Contestants"
          value={stats.contestants}
          color="primary"
        />
        <StatCard
          icon={<UserCheck className="h-6 w-6" />}
          label="Active Members"
          value={stats.members}
          color="success"
        />
        <StatCard
          icon={<Calendar className="h-6 w-6" />}
          label="Play Days"
          value={stats.playdays}
          color="accent"
        />
        <StatCard
          icon={<DollarSign className="h-6 w-6" />}
          label="Total Collected"
          value={`$${stats.totalFees.toFixed(2)}`}
          color="warning"
        />
      </div>

      {/* Recent Play Days */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-text-primary">Recent Play Days</CardTitle>
        </CardHeader>
        <CardContent>
          {recentPlaydays.length === 0 ? (
            <p className="text-text-secondary text-center py-8">No play days created yet</p>
          ) : (
            <div className="space-y-3">
              {recentPlaydays.map((playday) => (
                <div 
                  key={playday.id}
                  className="flex items-center justify-between p-4 bg-surface-muted rounded-lg"
                >
                  <div>
                    <p className="font-medium text-text-primary">{playday.name}</p>
                    <p className="text-sm text-text-secondary">{playday.date}</p>
                  </div>
                  <span className="text-sm text-text-muted">
                    {playday.event_ids.length} events
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-text-primary">Quick Start Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <QuickAction
              step="1"
              title="Setup Events"
              description="Configure your events and entry fees in Setup"
            />
            <QuickAction
              step="2"
              title="Create Play Day"
              description="Add a new play day date and select events"
            />
            <QuickAction
              step="3"
              title="Register & Check-In"
              description="Add contestants and process check-ins"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    primary: "bg-primary/20 text-primary",
    success: "bg-status-success/20 text-status-success",
    accent: "bg-accent/20 text-accent",
    warning: "bg-status-warning/20 text-status-warning"
  };

  return (
    <Card className="border-primary/20">
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-text-secondary text-sm">{label}</p>
            <p className="font-heading font-bold text-2xl text-text-primary">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickAction = ({ step, title, description }) => (
  <div className="p-4 border border-primary/20 rounded-lg">
    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mb-3">
      <span className="font-bold text-primary-foreground text-sm">{step}</span>
    </div>
    <h4 className="font-heading font-bold text-text-primary mb-1">{title}</h4>
    <p className="text-sm text-text-secondary">{description}</p>
  </div>
);

export default AdminHomePage;
